import type { FontFamilies } from "@react-three/uikit";
import { createContext, useContext } from "react";
import type { VirtualPointerEventsContextValue } from "./useVirtualPointerEvents";

export interface CRTInternalHandlers {
  onPointerMoveUV: (u: number, v: number) => void;
  onPointerClickUV: (u: number, v: number) => void;
  onPointerLeave: () => void;
}

export interface CRTContextValue {
  fontFamily?: string;
  fontFamilies?: FontFamilies;
  width: number;
  height: number;
  virtualPointerEvents: VirtualPointerEventsContextValue;
  /** Internal handlers - used by CRT.Screen for pointer events */
  _internal?: CRTInternalHandlers;
}

const CRTContextImpl = createContext<CRTContextValue | null>(null);

export const CRTContext = Object.assign(CRTContextImpl, {
  createContextValue: (
    virtualPointerEvents: Omit<
      VirtualPointerEventsContextValue,
      "onMouseMove" | "onMouseLeave"
    >,
    dimensions: { width: number; height: number },
    onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void,
    onMouseLeave: () => void,
    props: {
      fontFamily?: string;
      fontFamilies?: FontFamilies;
    },
  ): CRTContextValue => ({
    fontFamily: props.fontFamily,
    fontFamilies: props.fontFamilies,
    width: dimensions.width,
    height: dimensions.height,
    virtualPointerEvents: {
      ...virtualPointerEvents,
      onMouseMove,
      onMouseLeave,
    },
  }),
});

export function useCRT() {
  const context = useContext(CRTContext);
  if (!context) {
    throw new Error("useCRT must be used within a CRTScreen");
  }

  return context;
}
